import React from 'react';
import ReactDOM from 'react-dom';

import BrowserRouter from '../lib/router/BrowserRouter';

import App from '../components/app/App';
import AppProviders from '../components/app/AppProviders';
import ErrorBoundary from '../components/app/ErrorBoundary';

import routes from '../clientRoutes';
import router from '../lib/router/router';

import SettingsStore from '../lib/settings/SettingsStore';

import { biLogger, biTransaction } from './bi';

import settingsClient from './settingsClient';

import initTPAViewMode from '../lib/tpa/initTPAViewMode/initTPAViewMode';
import getFeedUrl from '../lib/settings/getFeedUrl';

import syncRouterWithBILogger from './syncRouterWithBILogger';

import { IRoutePageContext } from '../lib/router/types';
import { IInitialAppData } from '../types/types';
import { TPAViewMode, TPADeviceType } from '@wix/podcasts-common/dist/tpa';

interface IClientAppProps {
  appData: IInitialAppData;
  settingsStore: SettingsStore;
  ssr: boolean;
}

function ClientApp({ appData, settingsStore, ssr }: IClientAppProps) {
  const { context, routeData } = appData;

  // NOTE: use settings getter,
  // to be sure route `getInitialProps` will receive latest settings
  const routerContext: IRoutePageContext = {
    get settings() {
      return settingsStore.get();
    },
    config: context.config,
    biLogger,
    biTransaction,
  };

  return (
    <ErrorBoundary>
      <AppProviders
        i18n={context.i18n}
        settingsStore={settingsStore}
        experiments={context.experiments}
        tpa={context.tpa}
        config={context.config}
      >
        <App>
          <BrowserRouter
            context={routerContext}
            routes={routes}
            initialProps={routeData.props}
            ssr={ssr}
            allowCacheProps={true}
          >
            {({ Component, props }) => {
              if (!Component) {
                // As a part of generic errors fixing effort, we decided to remove 404 page as the one which normally can't be seen.
                // If you follow link with wrong route, it'll render site's 404 page, not podcasts' one
                return '';
              }
              return <Component {...props} />;
            }}
          </BrowserRouter>
        </App>
      </AppProviders>
    </ErrorBoundary>
  );
}

function onServicesReady(
  appData: IInitialAppData,
  settingsStore: SettingsStore,
) {
  const settings = settingsStore.get();
  biLogger.updateDefaults({
    feedId: getFeedUrl(settings, appData.context.config.defaultFeedUrl),
  });

  syncRouterWithBILogger();
  initTPAViewMode(appData.context.tpa.params, settingsStore);
}

function getSettings(viewMode: TPAViewMode, deviceType: TPADeviceType) {
  if (viewMode === TPAViewMode.STANDALONE) {
    return settingsClient.getDefaultSettings();
  }

  return settingsClient.getSettings(deviceType);
}

async function renderApp(appData: IInitialAppData) {
  const { context, routeData, ssr } = appData;
  const tpaParams = context.tpa.params;

  const settings = ssr
    ? context.settings
    : await getSettings(tpaParams.viewMode, tpaParams.deviceType);
  const settingsStore = new SettingsStore(settings);

  router.init({
    baseUrl: tpaParams.sectionUrl,
    target: tpaParams.target,
  });
  router.pushState(routeData.state);

  onServicesReady(appData, settingsStore);

  ReactDOM[ssr ? 'hydrate' : 'render'](
    <ClientApp {...{ appData, settingsStore, ssr }} />,
    document.getElementById('root'),
  );
}

export default renderApp;
